import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const VideoPopupContext = createContext(null);

export function VideoPopupProvider({ children }) {
  const [video, setVideo] = useState(null); // { youtubeId, title }

  const open = useCallback((data) => setVideo(data), []);
  const close = useCallback(() => setVideo(null), []);

  useEffect(() => {
    if (!video) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [video, close]);

  return (
    <VideoPopupContext.Provider value={{ open, close }}>
      {children}
      <AnimatePresence>
        {video && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="video-popup-backdrop"
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(20px)" }}
          >
            <motion.div
              className="relative w-full max-w-6xl"
              initial={{ scale: 0.92, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="video-popup"
            >
              {video.title && (
                <div className="flex items-center justify-between mb-4 px-1">
                  <div>
                    {video.eyebrow && <div className="label-mono text-cyan-300/80 mb-1">{video.eyebrow}</div>}
                    <div className="font-display text-xl md:text-2xl font-bold tracking-tight text-white">{video.title}</div>
                  </div>
                  <button
                    onClick={close}
                    className="glass-btn ghost p-2"
                    aria-label="Close"
                    data-testid="video-popup-close"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}
              <div
                className="relative w-full overflow-hidden rounded-2xl border border-white/10"
                style={{
                  aspectRatio: "16/9",
                  boxShadow: "0 0 60px rgba(0,184,255,0.15), inset 0 0 0 1px rgba(255,255,255,0.04)",
                }}
              >
                <iframe
                  title={video.title || "Video"}
                  src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  data-testid="video-popup-iframe"
                />
              </div>
              {!video.title && (
                <button
                  onClick={close}
                  className="absolute -top-12 right-0 glass-btn ghost p-2"
                  aria-label="Close"
                  data-testid="video-popup-close"
                >
                  <X size={18} />
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </VideoPopupContext.Provider>
  );
}

export function useVideoPopup() {
  return useContext(VideoPopupContext);
}
